import { postRequestWithRetry } from './asyncRequest';
import { logger } from './logger';
import { AjaxLandingMetricsPublisher } from './metrics';
import { BANNER_TYPE } from 'src/app/slice';

const landingAppEndpoint = '/ccx/ajax/setLanding';
/*
 * NOTE: these two requests previously passed `header` (singular) holding a
 * 'Content-type' value. axios only ever reads `headers`, so that value was never
 * sent -- confirmed by tsc, which flags it as TS2561 "did you mean 'headers'?"
 * once the config is typed. It was inert rather than harmful because both calls
 * post no body (only `params`), so no Content-Type was needed, which is why the
 * endpoint has always worked. The dead field is removed here rather than renamed:
 * `headers` on this object would be spread AFTER postRequest's own
 * `headers: { 'anti-csrftoken-a2z': ... }` and would overwrite the CSRF token.
 * Anyone restoring a Content-Type must merge into those headers, not replace them.
 */

interface SetToastBannerArgs {
  showToastBanner: boolean;
  toastBannerType: BANNER_TYPE;
}

interface SetLandingAppActionProps {
  appName: string;
  updateLandingAppName: (arg: string) => void;
  setToastBanner: (args: SetToastBannerArgs) => void;
}

interface UnsetLandingAppActionProps {
  updateLandingAppName: (arg: string) => void;
  setToastBanner: (args: SetToastBannerArgs) => void;
}

export const setLandingAppAction = async ({
  appName,
  updateLandingAppName,
  setToastBanner,
}: SetLandingAppActionProps): Promise<void> => {
  if (appName.length === 0) {
    return;
  }
  AjaxLandingMetricsPublisher.publishCounterMonitor('setRequest', 1);
  try {
    await postRequestWithRetry({
      params: { landingAppName: appName },
      url: landingAppEndpoint,
    });
    updateLandingAppName(appName);
    setToastBanner({
      showToastBanner: true,
      toastBannerType: BANNER_TYPE.SUCCESS,
    });
  } catch (error: any) {
    AjaxLandingMetricsPublisher.publishCounterMonitor('setError', 1);
    await logger.error(
      `Fail when setLandingAppAction to endpoint ${landingAppEndpoint} for appName ${appName} with error ${error}`,
    );
    setToastBanner({
      showToastBanner: true,
      toastBannerType: BANNER_TYPE.ERROR,
    });
  }
};

export const unsetLandingAppAction = async ({
  updateLandingAppName,
  setToastBanner,
}: UnsetLandingAppActionProps): Promise<void> => {
  AjaxLandingMetricsPublisher.publishCounterMonitor('unsetRequest', 1);
  try {
    await postRequestWithRetry({
      url: landingAppEndpoint,
    });
    updateLandingAppName('');
    setToastBanner({
      showToastBanner: true,
      toastBannerType: BANNER_TYPE.SUCCESS,
    });
  } catch (error: any) {
    await logger.error(
      `Fail when unsetLandingAppAction to endpoint ${landingAppEndpoint} with error ${error}`,
    );
    AjaxLandingMetricsPublisher.publishCounterMonitor('unsetError', 1);
    setToastBanner({
      showToastBanner: true,
      toastBannerType: BANNER_TYPE.ERROR,
    });
  }
};
